// The network, as shape rather than as feed.
//
// `src/data/network.json` is 225 stations with coordinates and the ordered
// stops of every line, pulled once from PTV by `scripts/fetch-network.mjs`.
// `src/data/network.sydney.json` is the same shape for Sydney Trains, and
// `network.brisbane.json`, `network.adelaide.json` and `network.canberra.json`
// for Queensland Rail, Adelaide Metro and the ACT light rail.
// Everything in this file is a pure function over ONE of those bundles — the
// one `currentCity()` names: no fetch, no store, no Vue. Two consequences,
// both deliberate.
//
//   * A POSITION NEVER LEAVES THE PHONE. "Which station am I standing at" is
//     answered by comparing a coordinate against the bundled ones, so there is
//     no endpoint to send it to and none is to be added.
//   * THE LINE STRIP WORKS OFFLINE, which is the state the app is most often
//     opened in — underground, on a platform, deciding whether to wait.
//
// EVERY BUNDLE IS LOADED ON DEMAND, ONE CHUNK PER CITY (`src/network-data.js`),
// and the city in force is awaited before the app mounts and before a switch,
// so every function here is still synchronous and still works offline: the
// bundle is in memory, and the service worker has its chunk cached. WHICH ONE
// IS READ is decided per
// call, and the indexes are rebuilt the first time a call arrives after the
// city changed. Rebuilding eagerly from `setCity` would mean that function had
// to know about every index in this file, and the one it forgot would be the
// one that went on answering with the old city's stations.
//
// Attribution stays with the data — PTV, TfNSW, Translink and Transport
// Canberra under CC BY 4.0, Adelaide Metro under its own terms — and is read off
// the bundle rather than written into a screen.

import { holdReloads } from './reload-guard.js';
import { currentCity, city as cityInfo } from './city.js';
import { loadedNetwork, loadNetwork, networkVersion } from './network-data.js';
import { normaliseStationName } from './departures.js';

// What a city that has not been loaded reads as: no stations, no lines.
const EMPTY = Object.freeze({ stations: [], lines: {} });

// Everything derived from a bundle, built once per city and dropped when the
// city changes. ONE object rather than five module-level maps, so "is this
// index stale" is one question with one answer.
let index = null;

function idx() {
  // Read for the dependency alone: a computed that asked while the bundle was
  // still on the way re-runs when it lands.
  void networkVersion.value;
  const id = currentCity();
  const bundle = loadedNetwork(id) ?? EMPTY;
  // Rebuilt when the bundle arrives as well as when the city changes: an index
  // made over EMPTY must not outlive the load that fills it.
  if (index?.city === id && index.bundle === bundle) return index;

  const info = cityInfo(id);

  const walkPartners = new Map();
  for (const [a, b] of info.walkLinks ?? []) {
    walkPartners.set(a, b);
    walkPartners.set(b, a);
  }

  index = {
    city: id,
    bundle,
    info,
    byId: new Map((bundle.stations ?? []).map((s) => [s.stopId, s])),
    cityEnd: new Set((info.cityEnd ?? []).map(normaliseStationName)),
    walkPartners,
  };
  return index;
}

/**
 * The whole bundle for the city in force.
 *
 * ONE READER, deliberately: `src/map-data.js`, which needs the stations and
 * the lines together to bind a line's stop list to its drawn track. Every
 * other screen asks this file a question instead of taking the data — that is
 * what the rest of this module is — and a second caller here would be the
 * start of the bundle's shape leaking into pages again.
 */
export function networkBundle() {
  return idx().bundle;
}

/** Required by the licence wherever this data is shown. */
export function attribution() {
  return idx().bundle.attribution ?? '';
}

/**
 * Whether this city's fares are priced on zones at all.
 *
 * Melbourne's are — Myki is priced on the highest zone TOUCHED, and the bundle
 * carries a zone for all 225 stations, which is what `src/fares.js` works on.
 * NOBODY ELSE'S ARE: Opal is priced on distance, Adelaide and Canberra are
 * flat-fare, and Translink's go card zones mean nothing to the Myki arithmetic.
 * So there is no zone to quote in four of the five cities, and the journey page's
 * own description of what it does must not promise one.
 *
 * Asked of the data rather than listed in `CITIES`, because the sentence it
 * governs is about whether the bundle can answer, and a flag saying "yes" over
 * a bundle with no zones in it would be the flag that was wrong.
 */
export function hasZones() {
  return allStations().some((s) => (s.zones?.length ?? 0) > 0);
}

/**
 * Every station in the city in force.
 *
 * A FUNCTION, where this used to be a constant array. The constant was built at
 * module load and handed out by reference, which is the one thing a second city
 * cannot survive: an importer holding Melbourne's 225 rows would go on
 * searching them after the switch, and the only symptom would be a Sydney
 * search that finds Flinders Street.
 */
export function allStations() {
  return idx().bundle.stations ?? [];
}

/** One station record, or null. */
export function stationById(stopId) {
  return idx().byId.get(Number(stopId)) ?? null;
}

/**
 * A station in a city that is NOT the one in force.
 *
 * The only reader is the Settings copy, which says where the board will open if
 * you switch — and that sentence has to name a station in the city you are
 * about to go to, which `stationById` by definition cannot reach. A linear scan
 * of the other bundle, once, for one sentence: building a second index to say
 * "Central" would cost more than it saves.
 */
export async function stationInCity(cityId, stopId) {
  const bundle = await loadNetwork(cityId);
  const id = Number(stopId);
  return (bundle?.stations ?? []).find((s) => s.stopId === id) ?? null;
}

/** The line's name, from the bundle rather than from the disruption row. */
export function lineName(routeId) {
  return idx().bundle.lines?.[String(routeId)]?.name ?? null;
}

/**
 * Everything the bundle says about one line, or null.
 *
 * The Sydney bundle carries `short` ("T1") and `colour`; the Melbourne one
 * carries neither, and its colours live in `METRO_LINES`. `lineList` in
 * app-state.js is where the two are reconciled; here they are only reported as
 * the bundle has them.
 */
export function lineRecord(routeId) {
  return idx().bundle.lines?.[String(routeId)] ?? null;
}

// ── V/Line between two metro stations ────────────────────────────────────────

/**
 * Stations on the metro map that the V/Line conditions treat as outside it.
 *
 * Conditions of travel 2.33: a "metropolitan railway station" is any on the
 * Melbourne Train Network Map OTHER THAN Ardeer, Caroline Springs, Deer Park,
 * Rockbank, Melton, Sunbury and Pakenham. The first five carry only V/Line
 * lines in the bundle, so the metro-line test below already leaves them out;
 * Sunbury and Pakenham are the terminuses of metro lines and have to be named.
 */
const VLINE_METRO_EXCEPTIONS = new Set(['Ardeer', 'Caroline Springs', 'Deer Park', 'Rockbank', 'Melton', 'Sunbury', 'Pakenham']);

/**
 * Whether V/Line's pick-up and set-down rules count this as a metropolitan
 * station: a Melbourne station some metro line serves, less the exceptions.
 * Any other city answers false, so the rule never reaches Sydney's regional
 * trains, which have no such condition.
 */
export function isMetroStation(stopId) {
  if (currentCity() !== 'melbourne') return false;
  const st = stationById(stopId);
  if (!st || VLINE_METRO_EXCEPTIONS.has(st.name)) return false;
  return (st.lines ?? []).some((id) => {
    const line = lineRecord(id);
    return line && !line.regional;
  });
}

/**
 * Whether a trip on this line from one station to the other is one the V/Line
 * conditions forbid (2.34 and 2.35): boarding at a metro station on a train
 * that ends at one, or alighting at a metro station from a train that does
 * not. For a single leg that is the same thing as "both ends are metro": an
 * outbound train may not set down at the second, an inbound one may not pick
 * up at the first. The ticket is void either way, so the app never offers it.
 */
export function vlineBarred(routeId, fromStopId, toStopId) {
  if (routeId == null || !lineRecord(routeId)?.regional) return false;
  return isMetroStation(fromStopId) && isMetroStation(toStopId);
}

/**
 * A line's stations in order, city end first.
 *
 * Returns station records, not ids — every caller wants the name, and a second
 * lookup at every call site is how a strip ends up with a hole in it where a
 * stop id has no station.
 */
export function lineStops(routeId) {
  const { bundle, byId } = idx();
  const line = bundle.lines?.[String(routeId)];
  if (!line) return [];
  return line.stops.map((id) => byId.get(id)).filter(Boolean);
}

/**
 * Stops PTV LISTS on a line that its ordinary trains do not call at.
 *
 * Written by `scripts/fetch-network.mjs` from the `NOT_CALLED_AT` table there,
 * and it is the other half of a correction rather than a leftover. `lineStops`
 * is where the line's trains go, and the planner, the strip and the reroute
 * engine are right to read nothing else. This is where a train on the line CAN
 * turn up anyway — Richmond and South Yarra on the Pakenham and Cranbourne
 * routes, which PTV lists and which only an event special calls at.
 *
 * It asserts nothing about today. A route listed here has no scheduled service
 * at that stop; whether one is running is a question only a live board answers,
 * and `src/specials.js` is what asks it.
 */
export function notCalledAt(routeId) {
  return idx().bundle.lines?.[String(routeId)]?.notCalledAt ?? [];
}

/**
 * The routes PTV lists at this stop whose ordinary trains do not call there.
 *
 * The same fact read from the station's end, and empty at all but a handful of
 * stations — which is the point: it is what lets a caller ask "could anything
 * unusual show up here" for nothing, and get "no" everywhere it matters.
 */
export function routesListedNotCalling(stopId) {
  const id = Number(stopId);
  if (!Number.isFinite(id)) return [];
  return Object.entries(idx().bundle.lines ?? {})
    .filter(([, line]) => (line.notCalledAt ?? []).includes(id))
    .map(([routeId]) => Number(routeId))
    .sort((a, b) => a - b);
}

/**
 * How long the timetable allows between two consecutive stops on a line, in
 * seconds, or null where the bundle does not know.
 *
 * `minutes[i]` is the gap from `stops[i]` to `stops[i + 1]`, written by
 * `scripts/fetch-network.mjs` from real run patterns: the shortest scheduled
 * gap seen across a handful of runs, so an express that skips the stop does
 * not stretch it. It is what the strip's dead reckoning paces a train by —
 * a train is expected to take the time the timetable gives it, not to do a
 * nominal speed over a chord.
 */
export function segmentSeconds(routeId, afterIndex) {
  const line = idx().bundle.lines?.[String(routeId)];
  const minutes = line?.minutes?.[Number(afterIndex)];
  return Number.isFinite(minutes) && minutes > 0 ? minutes * 60 : null;
}

/** Every route id the bundle knows the shape of. */
export function knownRouteIds() {
  return Object.keys(idx().bundle.lines ?? {}).map(Number);
}

/**
 * The lines implied by a set of stations.
 *
 * Choosing Carnegie is choosing Pakenham and Cranbourne — nobody stands on a
 * platform having opinions about route ids, and the welcome used to ask for
 * both halves separately because the code had no way of saying that. This is
 * that way.
 *
 * Takes station records in either of the two shapes the app carries them in:
 * the bundle's (`lines: [4, 11]`) and the store's, where the same field has
 * been through a database column and comes back `'4,11'`. Both are read, so a
 * caller holding a mixed list — saved rows beside freshly searched ones, which
 * is exactly what the welcome holds — does not have to normalise first.
 *
 * Pure, sorted, deduped, and filtered to lines this bundle knows the shape of.
 * An unknown id would be watched for ever with nothing to show for it: no
 * name, no strip, no disruption ever matching.
 */
export function linesForStations(rows = []) {
  const known = new Set(knownRouteIds());
  const out = new Set();
  for (const row of rows ?? []) {
    const raw = row?.lines;
    const ids = Array.isArray(raw) ? raw : String(raw ?? '').split(',');
    const here = ids.map((id) => Number(String(id).trim()))
      .filter((n) => Number.isInteger(n) && known.has(n));
    // A METRO STATION IMPLIES ITS METRO LINES ONLY. Southern Cross serves
    // every V/Line train in the state as well as four metro lines, and
    // choosing it in the welcome used to watch all twenty-five — Ararat,
    // Shepparton and Bairnsdale included — for somebody who catches the
    // Sunbury train. A regional line is watched by naming it, or by choosing
    // a station only regional trains reach, where it is the honest meaning.
    const metroHere = here.some((n) => !lineRecord(n)?.regional);
    for (const n of here) {
      if (metroHere && lineRecord(n)?.regional) continue;
      out.add(n);
    }
  }
  return [...out].sort((a, b) => a - b);
}

// ── Finding a station by name ────────────────────────────────────────────────

/** How many matches are worth showing before the list stops being a shortlist. */
const MAX_MATCHES = 8;

/**
 * Stations matching what someone has typed, best first. No network.
 *
 * The metropolitan network is 225 stations that change every few years, and it
 * already ships inside the app for the line strip and the nearest-station
 * check. Asking a server in Melbourne to search a list that is already on the
 * phone cost a round trip per keystroke, stopped working the moment the signal
 * did — on a platform, underground, which is where this app is used — and
 * returned exactly these rows. So it is answered here.
 *
 * Three tiers, in the order a person means them:
 *
 * 1. the exact name. Someone typing "Richmond" means Richmond, not East
 *    Richmond — and PTV's own ordering put it third, behind two stations they
 *    did not ask for.
 * 2. a name STARTING with what they typed. Half-typed names are the common
 *    case: "flind" is Flinders Street and nothing else.
 * 3. a name containing it anywhere, which is how "richmond" still reaches East
 *    Richmond once the station itself has been offered.
 *
 * Comparison is on `normaliseStationName`, so case, punctuation and the word
 * "Station" — which PTV appends to every stop and a person never types — are
 * not differences. Within a tier the bundle's own alphabetical order stands:
 * there is nothing to rank on locally and inventing a ranking would only move
 * rows around between keystrokes.
 */
export function searchStationsLocal(term) {
  const q = normaliseStationName(term);
  if (!q) return [];

  const exact = [];
  const prefix = [];
  const anywhere = [];

  for (const station of allStations()) {
    const name = normaliseStationName(station.name);
    if (!name) continue;
    if (name === q) exact.push(station);
    else if (name.startsWith(q)) prefix.push(station);
    else if (name.includes(q)) anywhere.push(station);
  }

  return [...exact, ...prefix, ...anywhere].slice(0, MAX_MATCHES);
}

// ── The city end ─────────────────────────────────────────────────────────────

/**
 * The stations that are "the city" rather than a suburb.
 *
 * In Melbourne: the loop (Flinders Street, Southern Cross, Flagstaff, Melbourne
 * Central, Parliament), the Metro Tunnel (Anzac, Town Hall, State Library,
 * Parkville, Arden) and North Melbourne, where the western lines fan out. In
 * Sydney: the CBD underground and Redfern.
 *
 * Which names those are is a fact about a city and not about this file, so it
 * lives in `CITIES[…].cityEnd` in city.js. They are collapsed on the strip
 * because a commuter reading it is asking "is the problem between me and the
 * city" — eleven rows spelling out the city is eleven rows of the answer they
 * already have.
 */
export function isCityEnd(station) {
  return idx().cityEnd.has(normaliseStationName(station?.name));
}

/**
 * Split a line's stops into its city block and the rest.
 *
 * Only the LEADING run counts. Every line in the bundle is stored city end
 * first, and a city station appearing later would be a data fault, not a
 * second city — shading one as "the city end" would put the collapsed row in
 * the middle of the suburbs.
 */
export function splitCityEnd(stops) {
  let i = 0;
  while (i < stops.length && isCityEnd(stops[i])) i += 1;
  return { city: stops.slice(0, i), rest: stops.slice(i) };
}

// ── Paired city stations ─────────────────────────────────────────────────────

/**
 * The city stations that are one interchange joined by a footway, not two.
 *
 * The Metro Tunnel was built under the loop, and two of its stations were built
 * INTO the loop station beside them: State Library shares a concourse with
 * Melbourne Central, Town Hall with Flinders Street. PTV models each pair as two
 * stops with two stop ids, which is true of the platforms and false of the
 * journey — a person changing there walks a subway for a couple of minutes and
 * never leaves the paid area.
 *
 * It matters because of trips like Carnegie to Merlynston. Every tunnel train
 * out of Carnegie continues as a Sunbury service — State Library, Parkville,
 * Arden, Footscray — and calls at NO station that shares a line with Upfield.
 * The static stop lists offer Parliament, which no tunnel train has ever
 * reached, so the honest answer used to be nothing at all. The real answer, and
 * the one Google gives, is the tunnel train to State Library and a hundred and
 * fifty metres on foot to Melbourne Central.
 *
 * Pairs by stop id, in `CITIES[…].walkLinks`, and Melbourne's two are asserted
 * in `tests/walk-links.test.js` against the bundle — a wrong id there would
 * quietly propose a walk between two stations on opposite sides of town.
 *
 * Sydney's list is EMPTY, which is an answer and not a gap: the CBD stations
 * are separate stations that happen to be close, and nothing in this spike
 * knows of a paid-area footway between two of them.
 */
export function walkLinks() {
  return idx().info.walkLinks ?? [];
}

/**
 * How long the walk takes, in minutes, for every pair.
 *
 * One number rather than one per pair: both subways are about 150 m, and the
 * difference between them is smaller than the difference between two people
 * walking them. Five minutes is the signed-walk time with a lift or an escalator
 * in it, which is the number a plan has to be right about — a connection missed
 * because the planner assumed a brisk two-minute stride is worse than one made
 * with three minutes to spare.
 */
export const WALK_MINUTES = 5;

/** The station on the other side of the subway, or null. */
export function walkPartner(stopId) {
  const partner = idx().walkPartners.get(Number(stopId));
  return partner === undefined ? null : stationById(partner);
}

// ── Through-running ──────────────────────────────────────────────────────────

/**
 * Lines that continue as another line past the city.
 *
 * In Melbourne, since the 1 February 2026 timetable, the Metro Tunnel carries
 * Sunbury trains through to become Cranbourne and Pakenham services and back,
 * and from mid-2026 Sandringham runs through to Werribee and Williamstown. A
 * Pakenham train at Carnegie is signed SUNBURY, so a strip that stops at the
 * city is showing half the journey the platform sign describes.
 *
 * Route ids, both directions, because the strip is drawn from either end. Which
 * pairs those are is a fact about a city, so the table is `CITIES[…].through`.
 *
 * SYDNEY'S IS EMPTY, and that is the truth rather than a hole: a T1 train is a
 * T1 train all the way round the City Circle. Every caller therefore has to
 * work with no continuations at all, which is the same code path Melbourne's
 * own Alamein and Glen Waverley have always taken.
 */
export function throughPairs() {
  const { info, bundle } = idx();
  // TWO SOURCES, ONE TABLE. `CITIES[…].through` is written by hand where the
  // operator's own data says nothing (Melbourne's tunnel pairs); a bundle's
  // `through` is DERIVED when the data does — Brisbane's route codes name both
  // ends of every journey (`FGBN`: Ferny Grove to Beenleigh), so the build
  // script reads the pairs off them. A city has one or the other. Merged
  // rather than chosen between so that neither can silently shadow the other.
  const derived = bundle?.through;
  if (!derived || !Object.keys(derived).length) return info.through ?? {};
  const merged = {};
  for (const table of [info.through ?? {}, derived]) {
    for (const [id, partners] of Object.entries(table)) {
      const list = (merged[id] ??= []);
      for (const other of partners ?? []) if (!list.includes(other)) list.push(other);
    }
  }
  return merged;
}

/**
 * What a line becomes on the far side of the city.
 *
 * Stops this line already lists are dropped, so the shared tunnel and loop
 * platforms are named once. What survives is the genuinely new half of the
 * journey — for Pakenham, Parkville and Arden onwards to Sunbury.
 */
export function throughContinuations(routeId) {
  const own = new Set(lineStops(routeId).map((s) => s.stopId));
  return (throughPairs()[Number(routeId)] ?? []).map((other) => ({
    routeId: other,
    name: lineName(other),
    stops: lineStops(other).filter((s) => !own.has(s.stopId)),
  })).filter((c) => c.name && c.stops.length);
}

// ── Branches ─────────────────────────────────────────────────────────────────

/**
 * The halves of a line the trunk does not draw.
 *
 * A BRANCH IS THE SAME LINE, WHICH IS WHAT MAKES IT DIFFERENT FROM A
 * CONTINUATION. A T1 train to Berowra is a T1 train — same number on the front,
 * same row in the ledger, same disruption notice — it simply leaves the trunk at
 * Central and goes the other way. A Pakenham train that becomes a Sunbury train
 * is two lines and one vehicle, which is why `throughContinuations` draws those
 * dimmed and in the OTHER line's colour, and why these are drawn in this line's.
 *
 * The bundle records `{ from, stops }` — the trunk station the branch leaves
 * from, and the stations it calls at after it — and this turns both into
 * records, because every caller wants names.
 *
 * MELBOURNE HAS NO `branches` KEY AT ALL. Its bundle is built by the other
 * script, from PTV, where through-running is the shape a line takes and a fork
 * is not; a missing key answers exactly as an empty list, so nothing about
 * Melbourne changes and nothing had to be added to its builder to say so.
 */
export function branchesFor(routeId) {
  const { byId } = idx();
  const line = lineRecord(routeId);
  return (line?.branches ?? []).map((branch) => {
    const stops = (branch.stops ?? []).map((id) => byId.get(id)).filter(Boolean);
    const from = byId.get(branch.from) ?? null;
    return {
      from: branch.from,
      fromName: from?.name ?? '',
      // What the junction row says: where this branch goes. The last stop is
      // the end of it, which is the name on the front of the train.
      name: stops.length ? stops[stops.length - 1].name : '',
      stops,
    };
  }).filter((b) => b.stops.length && b.fromName);
}

/**
 * Everything the strip draws for one line, in the order it is drawn.
 *
 * The city block is separated out because it collapses; the continuations are
 * separate because they are a different line and are drawn dimmer; the branches
 * are separate because they hang off a junction rather than off the end.
 */
export function stripFor(routeId) {
  const stops = lineStops(routeId);
  const { city, rest } = splitCityEnd(stops);
  return {
    city,
    stops: rest,
    continuations: throughContinuations(routeId),
    branches: branchesFor(routeId),
  };
}

// ── What a notice is about ───────────────────────────────────────────────────

/** Compare prose to station names on the same terms: no case, no "Station". */
function flatten(text) {
  return ` ${normaliseStationName(text)} `;
}

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Accept a route id, a line record, or a list of stops. */
function stopsOf(line) {
  if (Array.isArray(line)) return line.map((s) => (typeof s === 'object' ? s : stationById(s))).filter(Boolean);
  if (line && typeof line === 'object') return lineStops(line.routeId);
  return lineStops(line);
}

/**
 * The stops a notice is about, as an ordered list of stop ids.
 *
 * Two readings, in order of how much they claim:
 *
 * 1. "between X and Y" — PTV's own phrasing for a closed SECTION, and the one
 *    that answers the strip's question. Everything from X to Y inclusive.
 * 2. Otherwise, any station the prose names. A car-park notice names one
 *    station and means one station.
 *
 * And a third answer that matters more than either: NOTHING. Most of the feed
 * is prose about a line with no station in it — "delays due to a fault in the
 * Craigieburn area" names Craigieburn and nothing else, and "trains are running
 * to an altered timetable" names no station at all. Shading a plausible guess
 * would tell a commuter the problem is between them and the city on no
 * evidence, which is the one thing this strip must never do. An empty answer
 * draws no shading.
 *
 * The between-pair is matched by trying the line's OWN station names against
 * the sentence rather than by capturing whatever sits between "between" and
 * "and". A capture reads "between Caulfield and Dandenong following a signal
 * fault" as a station called "Dandenong following a signal fault"; the names
 * are a closed set of 225, so asking them is both cheaper and exact.
 */
export function affectedStops(line, text) {
  const stops = stopsOf(line);
  if (!stops.length) return [];

  const flat = flatten(text);
  if (flat.trim().length < 2) return [];

  const section = betweenPair(stops, flat);
  if (section) {
    const [a, b] = section;
    const lo = Math.min(a, b);
    const hi = Math.max(a, b);
    return stops.slice(lo, hi + 1).map((s) => s.stopId);
  }

  return namedStops(stops, flat);
}

/** Indices of the two stations in a "between X and Y", or null. */
function betweenPair(stops, flat) {
  if (!/\bbetween\b/.test(flat)) return null;
  for (let i = 0; i < stops.length; i += 1) {
    for (let j = 0; j < stops.length; j += 1) {
      if (i === j) continue;
      const a = normaliseStationName(stops[i].name);
      const b = normaliseStationName(stops[j].name);
      if (!a || !b) continue;
      const re = new RegExp(`\\bbetween\\s+${escapeRe(a)}\\s+and\\s+${escapeRe(b)}\\b`);
      if (re.test(flat)) return [i, j];
    }
  }
  return null;
}

/**
 * Every station the prose names, longest name first.
 *
 * Longest first and then struck out of the working text, because "East
 * Richmond" contains "Richmond" and a notice about one is not a notice about
 * the other. Matching short names first shades both and puts the fault a
 * station closer to town than it is.
 */
function namedStops(stops, flat) {
  const byLength = [...stops]
    .map((s, index) => ({ index, stopId: s.stopId, norm: normaliseStationName(s.name) }))
    .filter((s) => s.norm)
    .sort((x, y) => y.norm.length - x.norm.length);

  let working = flat;
  const hits = [];
  for (const s of byLength) {
    if (!working.includes(` ${s.norm} `)) continue;
    working = working.split(` ${s.norm} `).join('  ');
    hits.push(s);
  }
  return hits.sort((x, y) => x.index - y.index).map((s) => s.stopId);
}

// ── Where you are standing ───────────────────────────────────────────────────

const EARTH_METRES = 6_371_008.8;
const rad = (deg) => (deg * Math.PI) / 180;

/** Great-circle distance in metres. A city is flat enough that this is exact. */
export function metresBetween(aLat, aLng, bLat, bLng) {
  const dLat = rad(bLat - aLat);
  const dLng = rad(bLng - aLng);
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(rad(aLat)) * Math.cos(rad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_METRES * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * The n nearest stations to a position, nearest first.
 *
 * A linear scan of a few hundred rows. An index would be faster and would need
 * to be kept in step with the bundle, which is a second thing to get wrong for
 * a saving nobody can perceive.
 */
export function nearestStations(lat, lng, n = 3) {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return [];
  return allStations()
    .map((station) => ({ station, metres: metresBetween(lat, lng, station.lat, station.lng) }))
    .sort((a, b) => a.metres - b.metres)
    .slice(0, Math.max(0, n));
}

/** Metres under a kilometre, kilometres with one decimal above it. */
export function formatDistance(metres) {
  const m = Number(metres);
  if (!Number.isFinite(m)) return '';
  return m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(1)} km`;
}

/**
 * How close you have to be before the app offers to switch the board.
 *
 * 800m is about a ten-minute walk: close enough that you are plausibly heading
 * for that station, far enough to survive a coarse fix on a phone in a pocket.
 */
export const NEAR_METRES = 800;

/**
 * How far a station's board will point to the nearest stop of a mode that has
 * none beside it ("No tram stop at Carnegie · Nearest: Route 67 · 520 m"). Past
 * this the board only says there is none: a walk of over a quarter of an hour is
 * not a place to send someone from a departures screen.
 */
export const NEAREST_STOP_METRES = 1000;

/** Walking pace the board quotes, in metres a minute (the signed-walk figure). */
export const WALK_METRES_PER_MINUTE = 80;

/**
 * The stations to offer under the board, as a decision on its own.
 *
 * Never a switch — an offer. A commuter has a home station and a work station,
 * and being near one of them is a hint, not an instruction: switching silently
 * would mean somebody glancing at the board reads the wrong platform for the
 * wrong suburb, which is the exact failure the stop-id repair exists for.
 *
 * Up to two, because in the city three stations sit within 500 m of each
 * other and "nearest" is a coin toss between Parliament and Melbourne Central;
 * the second chip is the one that is usually right. The station already on the
 * board is never offered. Returns an empty list when there is nothing to say,
 * which is most of the time.
 */
export const NEARBY_CHIPS = 2;

export function nearbyChips({ nearby, currentStopId, limit = NEARBY_CHIPS } = {}) {
  return (Array.isArray(nearby) ? nearby : [])
    .filter((n) => n?.station?.name)
    .filter((n) => n.metres != null && Number.isFinite(Number(n.metres)) && Number(n.metres) <= NEAR_METRES)
    .filter((n) => Number(n.station.stopId) !== Number(currentStopId))
    .slice(0, limit)
    .map((n) => ({ station: n.station, metres: Number(n.metres), label: `${n.station.name} · ${formatDistance(n.metres)}` }));
}

/** The single-chip form, kept for callers and tests that want just the nearest. */
export function nearbyChip({ nearest, metres, currentStopId } = {}) {
  const [first] = nearbyChips({ nearby: [{ station: nearest, metres }], currentStopId, limit: 1 });
  return first ? { ...first, label: `You’re near ${first.station.name} · show it` } : null;
}

// ── The device ───────────────────────────────────────────────────────────────
//
// The two impure functions in this file, and they live here rather than in a
// module of their own because the alternative is four copies of the same
// guarded import — in the station picker, the welcome, Settings and the
// disruption screen — each of which is a chance to forget one of the guards and
// ship a web build that throws on a plugin that only exists on a phone.
//
// Both follow the pattern `localNotifications()` in app-state.js sets: the
// plugin is IMPORTED, lazily, and only on a native platform. Importing it at
// the top of the file would put native-only code in the web bundle and turn a
// feature that should degrade into a hard failure at first use.

import { isNative } from './core/native.js';
import { prefs } from './core/storage.js';
// Static imports: a dynamic import() of a Capacitor plugin never settles inside
// the iOS WebView (see localNotifications in app-state.js), so the plugins are
// loaded with the module and only CALLED on a native platform.
import { Geolocation } from '@capacitor/geolocation';
import { Browser } from '@capacitor/browser';

/** The operator's own network map. Their artwork, so it is linked, not bundled. */
export function networkMapUrl() {
  return idx().info.mapUrl;
}

const LOCATION_PREF = 'lateagain:location';

/** True once the person has granted location at least once on this device. */
export function locationGranted() {
  return prefs[LOCATION_PREF] === true;
}

export function rememberLocation(granted) {
  prefs[LOCATION_PREF] = granted === true;
}

// Synchronous, and a plain object — never the plugin proxy itself. A Capacitor
// proxy answers `.then` with a native call, so awaiting it or returning it
// from an async function hangs for ever (see localNotifications in
// app-state.js).
function nativeGeolocation() {
  if (!isNative) return null;
  const p = window.Capacitor?.Plugins?.Geolocation ?? Geolocation;
  return {
    checkPermissions: (...a) => p.checkPermissions(...a),
    requestPermissions: (...a) => p.requestPermissions(...a),
    getCurrentPosition: (...a) => p.getCurrentPosition(...a),
  };
}

/**
 * Eight seconds is right for a phone and wrong for a browser.
 *
 * On a phone the OS prompt is answered before `getCurrentPosition` is ever
 * called — the plugin asks for the permission first and awaits the answer —
 * so the timeout only bounds the fix itself, and a commuter asking "which
 * station am I at" has already answered it by looking up.
 *
 * ON THE WEB THERE IS NO SEPARATE ASK. `navigator.geolocation` raises the
 * permission bubble itself and calls NEITHER callback until it is answered, so
 * the timeout is the whole interval a person has to read a box and press a
 * button. Eight seconds of that is a refusal the app invented on their behalf.
 */
const NATIVE_TIMEOUT_MS = 8_000;
const WEB_TIMEOUT_MS = 25_000;

/**
 * What a failure actually was, in one word.
 *
 * 1 is PERMISSION_DENIED, 2 POSITION_UNAVAILABLE and 3 TIMEOUT in the web API;
 * the plugin says the same things in words. The distinction that matters is
 * the one between 1 and 3: only the first is somebody saying no.
 */
function reasonFor(e) {
  const message = String(e?.message ?? '');
  if (/secure origin|secure context/i.test(message)) return 'insecure';
  if (e?.code === 1 || /denied|permission/i.test(message)) return 'denied';
  if (e?.code === 3 || /timed? ?out/i.test(message)) return 'timeout';
  return 'failed';
}

/**
 * What the browser already knows, before anything is asked.
 *
 * A site that has been refused once stays refused, and `getCurrentPosition`
 * answers that case by raising no bubble and waiting out the whole timeout —
 * twenty-five seconds of a disabled button to deliver an answer the Permissions
 * API had at hand. Not every browser implements the query for geolocation, so
 * an unknown answer means "ask properly", never "give up".
 */
async function permissionState() {
  try {
    const status = await navigator.permissions?.query({ name: 'geolocation' });
    return status?.state ?? null;
  } catch {
    return null;
  }
}

/**
 * Where the phone is, once.
 *
 * Coarse — `enableHighAccuracy: false`. A station is 200 metres of platform and
 * the nearest one does not change if the fix is fifty metres out, while the
 * precise fix costs GPS time on a phone in a pocket underground.
 *
 * Returns a reason rather than throwing, because every caller has something to
 * SAY about each outcome and none of them can recover from any of them. The
 * reasons are `denied`, `timeout`, `insecure`, `unsupported`, `unavailable` and
 * `failed`, and `locationSays` in src/welcome.js is where each becomes a
 * sentence. They are NOT interchangeable: a browser that never answered has
 * not refused, and telling somebody it did sends them to a settings screen to
 * turn off a permission they never granted.
 *
 * The coordinates go into `nearestStations` and nowhere else. There is no
 * endpoint that takes them and none is to be added — that is the whole reason
 * the network is bundled.
 */
export async function findPosition() {
  // Same reason as the notification prompt: see src/reload-guard.js.
  const release = holdReloads();
  try {
    return await findPositionNow();
  } finally {
    release();
  }
}

/**
 * Where the phone is, but ONLY if it has already said yes.
 *
 * For screens that offer "near me" as a convenience and must not be the thing
 * that raises a permission box: the answer is read first (the plugin's
 * `checkPermissions` on a phone, the Permissions API on the web) and anything
 * but a standing grant is `notasked`, with no prompt and no fix. A browser that
 * cannot be asked what it has answered is treated the same way: the button
 * that does ask is one tap away.
 */
export async function findPositionIfGranted() {
  const plugin = nativeGeolocation();
  if (plugin) {
    try {
      const status = await plugin.checkPermissions();
      const state = status?.location ?? status?.coarseLocation;
      if (state !== 'granted') return { ok: false, reason: 'notasked' };
    } catch {
      return { ok: false, reason: 'notasked' };
    }
    return findPosition();
  }
  if (isNative) return { ok: false, reason: 'notasked' };
  if (typeof navigator === 'undefined' || !navigator.geolocation) return { ok: false, reason: 'notasked' };
  if ((await permissionState()) !== 'granted') return { ok: false, reason: 'notasked' };
  return findPosition();
}

async function findPositionNow() {
  const plugin = nativeGeolocation();
  if (plugin) {
    try {
      const status = await plugin.requestPermissions();
      const state = status?.location ?? status?.coarseLocation;
      if (state === 'denied') return { ok: false, reason: 'denied' };
      const pos = await plugin.getCurrentPosition({
        enableHighAccuracy: false,
        timeout: NATIVE_TIMEOUT_MS,
      });
      return { ok: true, lat: pos.coords.latitude, lng: pos.coords.longitude };
    } catch (e) {
      return { ok: false, reason: reasonFor(e) };
    }
  }

  // A WebView with no plugin. NOT the web path: iOS never calls either callback
  // for `navigator.geolocation` inside a WebView, so a native build taking it
  // would sit on "Finding you…" for ever with no error to show.
  if (isNative) return { ok: false, reason: 'unavailable' };

  return webPosition();
}

/**
 * The browser's own geolocation, with its three real constraints answered.
 *
 * A SECURE CONTEXT, first, because an insecure origin is the one failure the
 * person cannot fix from inside the page and the one the browser reports as a
 * refusal. Chrome rejects with PERMISSION_DENIED and the words "Only secure
 * origins are allowed", which read through the old classifier as somebody
 * pressing Block — so the app told people they had said no on a page where the
 * question was never asked. It is checked before the call rather than after it
 * so the answer is instant and the wording is about the connection.
 *
 * A STANDING REFUSAL, second, read from the Permissions API rather than
 * discovered by waiting out the timeout.
 *
 * AND A BUBBLE NOBODY ANSWERS, third, which is the only case left and the one
 * the timeout exists for. It is reported as `timeout`, honestly, rather than as
 * a refusal: the box may still be open on their screen.
 */
async function webPosition() {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    return { ok: false, reason: 'unsupported' };
  }
  if (typeof window !== 'undefined' && window.isSecureContext === false) {
    return { ok: false, reason: 'insecure' };
  }
  if ((await permissionState()) === 'denied') return { ok: false, reason: 'denied' };

  try {
    const pos = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: false,
        timeout: WEB_TIMEOUT_MS,
        // A fix from the last minute is the same fix. It saves the second
        // press of a button on a page that asks twice.
        maximumAge: 60_000,
      });
    });
    return { ok: true, lat: pos.coords.latitude, lng: pos.coords.longitude };
  } catch (e) {
    return { ok: false, reason: reasonFor(e) };
  }
}

/** Open a page outside the app: the in-app browser natively, a tab on the web. */
export async function openExternal(url) {
  if (isNative) {
    try {
      await (window.Capacitor?.Plugins?.Browser ?? Browser).open({ url });
      return;
    } catch { /* plugin missing in this build — fall through to a plain tab */ }
  }
  if (typeof window !== 'undefined') window.open(url, '_blank', 'noopener');
}
